import { useCallback, useEffect, useRef } from 'react';
import { FlatList, LayoutChangeEvent, NativeScrollEvent, NativeSyntheticEvent } from 'react-native';
import { MIN_SUB_ROWS_VISIBLE_ON_EXPAND, AUTO_SCROLL_BOTTOM_PADDING } from '@components/settings/components/common/animatedTable/constants';
import { getRowAccordionData, isRowExpanded } from '@components/settings/components/common/animatedTable/helpers/accordionRowState';
import { UseAccordionAutoScrollParams, UseAccordionAutoScrollResult } from '@components/settings/components/common/animatedTable/types';

export const useAccordionAutoScroll = <T,>({
  enabled,
  array,
  expandedRows,
  scaledLineSize,
  getRowKey,
  getAccordionItemsCount,
}: UseAccordionAutoScrollParams<T>): UseAccordionAutoScrollResult<T> => {
  const flatListRef = useRef<FlatList<T>>(null);
  const scrollOffsetRef = useRef<number>(0);
  const viewportHeightRef = useRef<number>(0);
  const prevExpandedCountsRef = useRef<Map<string, number>>(new Map());

  const onScroll = useCallback((e: NativeSyntheticEvent<NativeScrollEvent>) => {
    scrollOffsetRef.current = e.nativeEvent.contentOffset.y;
  }, []);

  const onLayout = useCallback((e: LayoutChangeEvent) => {
    viewportHeightRef.current = e.nativeEvent.layout.height;
  }, []);

  useEffect(() => {
    if (!enabled || !expandedRows || !getRowKey || !getAccordionItemsCount) return;

    const currentKeys = Object.keys(expandedRows).filter((k) => isRowExpanded(expandedRows[k]));
    const currentState = new Map<string, number>();
    let triggerKey: string | null = null;

    for (const key of currentKeys) {
      const itemIndex = array.findIndex((item) => String(getRowKey(item)) === key);
      if (itemIndex < 0) {
        currentState.set(key, 0);
        continue;
      }
      const accordionData = getRowAccordionData(expandedRows[key]);
      const currentCount = getAccordionItemsCount(accordionData) ?? 0;
      currentState.set(key, currentCount);

      const prevCount = prevExpandedCountsRef.current.get(key);
      if (prevCount === undefined && triggerKey === null) {
        triggerKey = key;
      } else if (prevCount !== undefined && prevCount === 0 && currentCount > 0 && triggerKey === null) {
        triggerKey = key;
      }
    }

    prevExpandedCountsRef.current = currentState;

    if (triggerKey === null) return;
    if (viewportHeightRef.current === 0) return;

    const expandedIndex = array.findIndex((item) => String(getRowKey(item)) === triggerKey);
    if (expandedIndex < 0) return;

    const expandedData = getRowAccordionData(expandedRows[triggerKey]);
    const subRowCount = getAccordionItemsCount(expandedData) ?? 0;
    if (subRowCount === 0) return;

    let rowTopEstimate = 0;
    for (let i = 0; i < expandedIndex; i++) {
      const aboveItem = array[i];
      const aboveKey = String(getRowKey(aboveItem));
      const aboveExpanded = isRowExpanded(expandedRows[aboveKey]);
      const aboveData = getRowAccordionData(expandedRows[aboveKey]);
      const aboveSubRows = aboveExpanded ? (getAccordionItemsCount(aboveData) ?? 0) : 0;
      rowTopEstimate += scaledLineSize + aboveSubRows * scaledLineSize;
    }

    const subRowsToShow = Math.min(subRowCount, MIN_SUB_ROWS_VISIBLE_ON_EXPAND);
    const estimatedTargetBottom = rowTopEstimate + scaledLineSize + subRowsToShow * scaledLineSize + AUTO_SCROLL_BOTTOM_PADDING;
    const viewportBottom = scrollOffsetRef.current + viewportHeightRef.current;

    if (estimatedTargetBottom <= viewportBottom) return;

    const reservedBelowRow = (1 + subRowsToShow) * scaledLineSize + AUTO_SCROLL_BOTTOM_PADDING;
    const desiredOffsetAbove = Math.max(0, viewportHeightRef.current - reservedBelowRow);
    const viewPosition = desiredOffsetAbove / viewportHeightRef.current;

    flatListRef.current?.scrollToIndex({ index: expandedIndex, viewPosition, animated: true });
  }, [enabled, array, expandedRows, scaledLineSize, getRowKey, getAccordionItemsCount]);

  return { flatListRef, onScroll, onLayout };
};
